import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { ArrowDownWhiteIcon, ConnectionAppLogo, DownloadArrowDown, ViewerAppLogo } from "../../assets/icons";
import { UserInformations } from "../../types/User";
import { useParams } from "react-router-dom";
import { useLazyFetchIdeaConPublicUrlQuery } from "../../services/clApiSlice";
import { ConDesignItemWithPicture } from "../../types/ConDesignItem";
import { toast } from "react-hot-toast";

interface OpenInDesktopButtonProps {
	version: number;
	userInformations: UserInformations | undefined;
	designItem: ConDesignItemWithPicture | undefined;
	setProjectDownloadsExceeded: (exceeded: boolean) => void;
}

const OpenInDesktopButton = ({
	version,
	userInformations,
	designItem,
	setProjectDownloadsExceeded,
}: OpenInDesktopButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { itemId } = useParams();
	const toggleDropdown = () => setIsOpen(!isOpen);
	
	const [fetchIdeaConUrlTrigger] = useLazyFetchIdeaConPublicUrlQuery();
	
	const openDesktopAppHandler = async () => {
		if (itemId) {
			try {
				const ideaConUrl = await fetchIdeaConUrlTrigger({
					itemId,
					loggingPurpose: "OpenInDesktop",
				}, false).unwrap();

				window.location.href = `ideacon:${ideaConUrl.url}`;

				if (userInformations?.userIds.licenseId && userInformations.userIds.userId) {
					window.dataLayer.push({
						"event": "cl_open_to_desktop_initiated",
						"license_id": userInformations.userIds.licenseId,
						"user_id": userInformations.userIds.userId,
						"con_design_item_id": designItem?.designItem.conDesignItemId,
						"con_design_set_id": designItem?.designItem.conDesignSetId,
					});
				}
				
				const message = (
					<FormattedMessage
						id="OpenInDesktop_toast_message"
						defaultMessage="Attempting to open desktop application."
					/>
				);
				
				toast(message);
				
				setIsOpen(false);
			} catch (error: any) {
				if (error?.status === 429) {
					setProjectDownloadsExceeded(true);
				}
				
				const message = (
					<FormattedMessage
						id="OpenIdeaCon_toast_error_message"
						defaultMessage="Failed to launch desktop application"
					/>
				);
				
				toast.error(message);
			}
		}
	};
	
	const downloadHandler = async () => {
		if (itemId) {
			try {
				const message = (
					<FormattedMessage
						id="DownloadIdeaCon_toast_message"
						defaultMessage="Attempting to download IdeaCon file."
					/>
				);
				
				toast(message);
				
				const response = await fetchIdeaConUrlTrigger({
					itemId,
					loggingPurpose: "ItemDownload",
				}, false).unwrap();
				
				const fetchResponse = await fetch(response.url);
				const blob = await fetchResponse.blob();
				const downloadUrl = URL.createObjectURL(new Blob([blob], { type: blob.type }));
				
				const filename = `${itemId}.ideacon`;
				
				if (userInformations?.userIds.licenseId && userInformations.userIds.userId) {
					window.dataLayer.push({
						"event": "cl_download_item_initiated",
						"license_id": userInformations.userIds.licenseId,
						"user_id": userInformations.userIds.userId,
						"con_design_item_id": designItem?.designItem.conDesignItemId,
						"con_design_set_id": designItem?.designItem.conDesignSetId,
					});
				}
				
				const link = document.createElement("a");
				link.href = downloadUrl;
				link.setAttribute("download", filename);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				
				
			} catch (error: any) {
				if (error?.status === 429) {
					setProjectDownloadsExceeded(true);
				}
				const message = (
					<FormattedMessage
						id="DownloadIdeaCon_toast_error_message"
						defaultMessage="Download failed"
					/>
				);
				
				toast.error(message);
			}
		}
	};
	
const viewerPath = `/openFromConnectionLibrary/${designItem?.designItem.conDesignItemId}`;

const viewerHandler = () => {
	const urlString = window.location.href;
	let viewerUrl;

	if (urlString.indexOf("localhost") !== -1) {
		viewerUrl = "http://localhost:5173";
	} else if (urlString.indexOf("staging") !== -1) {
		viewerUrl = "https://staging1.viewer.ideastatica.com";
	} else {
		viewerUrl = "https://viewer.ideastatica.com";
	}

	window.open(`${viewerUrl}${viewerPath}`, "_blank");
};
	
	return (
		<>
			{version && version >= 6 && (
				<div className="openInDesktop-dropdown">
					<button className="openInDesktop-btn" onClick={openDesktopAppHandler}>
                        <span className="dropbtn-span">
                            <FormattedMessage id="OpenInDesktop" defaultMessage="Open in Desktop" />
                        </span>
					</button>
					<button className="openInDesktop-btn openInDesktop-dropbtn" onClick={toggleDropdown}>
						<div className="dropbtn-arrow">
							<ArrowDownWhiteIcon />
						</div>
					</button>
					{isOpen && (
						<div className="openInDesktop-dropdown-content">
							<button onClick={openDesktopAppHandler}>
								<ConnectionAppLogo />
								<span>
                                    <FormattedMessage id="OpenInDesktop" defaultMessage="Open in Desktop" />
                                </span>
							</button>
							<button onClick={downloadHandler}>
								<DownloadArrowDown />
								<span>
                                    <FormattedMessage id="Download" defaultMessage="Download" />
                                </span>
							</button>
							<button onClick={viewerHandler} data-viewer-path={viewerPath}>
								<ViewerAppLogo />
								<span>
										<FormattedMessage id="OpenInViewer" defaultMessage="Open in Viewer" />
								</span>
							</button>
						</div>
					)}
				</div>
			)}
		</>
	);
};

export default OpenInDesktopButton;
